import type { QualityEnvelope } from './types';

/**
 * The data notes, in the order the sheet lists them: 1 mixed snapshot, 2 test accounts unchecked, 3 stale address
 * list, 4 unreadable address list, 5 no public or internal address, 6 internal addresses counted, 7 old or unchecked
 * data age, 8 paired visit check off, 9 the last numbers kept while asking again.
 */
export type DataNoteId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

/** One active note: its copy (an English key) and the `{{n}}` it reads, when it has one. */
export type DataNote = { id: DataNoteId; text: string; n?: number };

const MINUTE_MS = 60_000;
const STALE_AFTER_MS = 15 * MINUTE_MS;

/** How old the data was when the answer was made; null when that could not be checked. */
const dataAgeMs = (meta: QualityEnvelope['meta']): number | null => {
  const started = meta.refreshObservation.lastSuccessfulRefreshStartedAt;
  if (meta.refreshObservation.status === 'unavailable' || started === null) return null;
  const age = Date.parse(meta.generatedAt) - Date.parse(started);
  return Number.isNaN(age) ? null : age;
};

/** The notes an answer raises; `held` when the figures in view are an earlier read's (note 9). */
export const dataNotes = (answer: QualityEnvelope, held: boolean): DataNote[] => {
  const { meta, hostCatalog } = answer;
  const notes: DataNote[] = [];
  if (meta.snapshot === 'mixed')
    notes.push({
      id: 1,
      text: 'Some numbers changed while they were read and may not match. Reload to read them again.',
    });
  if (meta.accountEvidence.nativeTest === 'unavailable')
    notes.push({ id: 2, text: 'Test accounts could not be checked, so test visits may be counted.' });
  if (hostCatalog.status === 'stale')
    notes.push({
      id: 3,
      text: 'Your address list could not be refreshed, so public and internal addresses come from the last known list.',
    });
  if (hostCatalog.status === 'unavailable')
    notes.push({ id: 4, text: 'Your address list could not be read. Try again in a few minutes.' });
  else if (hostCatalog.defaultBasis === 'none')
    notes.push({ id: 5, text: 'No public address yet. Choose addresses to see their numbers.' });
  if (hostCatalog.defaultBasis === 'internal')
    notes.push({ id: 6, text: 'No public address yet, so these numbers count your internal addresses.' });
  const age = dataAgeMs(meta);
  if (age === null) notes.push({ id: 7, text: 'We could not check how fresh these numbers are.' });
  else if (age > STALE_AFTER_MS)
    notes.push({
      id: 7,
      text: 'These numbers are {{n}} minutes old. Newer visits show after the next update.',
      n: Math.floor(age / MINUTE_MS),
    });
  if (meta.pairedSiteLanding === 'disabled')
    notes.push({ id: 8, text: 'One automation check is off: paired website and landing visits are not checked.' });
  if (held) notes.push({ id: 9, text: 'Showing the last numbers while we ask again.' });
  return notes;
};

/** The Data notes button's tone: a warning for any note but 8, quiet otherwise. */
export const notesTone = (notes: readonly DataNote[]): 'warning' | 'quiet' =>
  notes.some((n) => n.id !== 8) ? 'warning' : 'quiet';
